import { useState, useMemo } from "react";
import { ITask } from "@/interfaces/dashboard.interface";
import { SectionCard } from "@/components/ui/section-card";
import StatusBadge from "./status-badge";
import Pagination from "./pagination";
import { Input } from "../ui/input";
import { Skeleton } from "../ui/skeleton";
import { Search, ChevronRight, Eye, Pencil, Trash2, CheckCircle2, Circle } from "lucide-react";
import { Link } from "wouter";

interface TasksTableProps {
  tasks: ITask[];
  isLoading?: boolean;
  onViewTask?: (taskId: string) => void;
  onEditTask?: (taskId: string) => void;
  onDeleteTask?: (taskId: string) => void;
  onToggleTask?: (taskId: string) => void;
}

export default function TasksTable({
  tasks,
  isLoading,
  onViewTask,
  onEditTask,
  onDeleteTask,
  onToggleTask
}: TasksTableProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 4;

  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return tasks;
    return tasks.filter(
      (task) =>
        task.taskName.toLowerCase().includes(term) ||
        task.responsible.name.toLowerCase().includes(term) ||
        task.project.toLowerCase().includes(term) ||
        task.stage.toLowerCase().includes(term)
    );
  }, [tasks, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));

  const paginated = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize]);

  return (
    <SectionCard className="p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
        <h2 className="text-lg font-bold text-white">Tasks de Projetos</h2>

        <div className="flex items-center gap-3">
          {/* Search */}
          <Input
            type="text"
            placeholder="Procurar..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bg-[#0C1427] border border-[#5D5D5D] text-white placeholder:text-gray-400"
            end={<Search size={18} />}
          />

          {/* Ver mais */}
          <Link href="/documentacao/gestao-tarefas">
            <button
              className="border border-[#5D5D5D] rounded-[10px] px-5 py-[10px] flex items-center gap-1 hover:bg-white/5 transition-colors"
            >
              <p className="text-xs text-[#F6F6F6]">Ver mais</p>
              <ChevronRight className="size-4 text-[#F6F6F6]" />
            </button>
          </Link>
        </div>
      </div>

      {/* Table Header */}
      <div className="bg-[#0C1427] rounded-lg px-5 py-[10px] mb-1 hidden md:grid grid-cols-[122px_154px_81px_120px_91px_91px_123px] gap-[59px]">
        <p className="text-xs font-bold text-[#F6F6F6]">Tasks</p>
        <p className="text-xs font-bold text-[#F6F6F6]">Responsável</p>
        <p className="text-xs font-bold text-[#F6F6F6]">Data</p>
        <p className="text-xs font-bold text-[#F6F6F6]">Etapa</p>
        <p className="text-xs font-bold text-[#F6F6F6]">Status</p>
        <p className="text-xs font-bold text-[#F6F6F6]">Projeto</p>
        <p className="text-xs font-bold text-[#F6F6F6]"></p>
      </div>

      {/* Table Rows */}
      <div className="space-y-0">
        {isLoading ? (
          <div className="space-y-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-[64px]" />
            ))}
          </div>
        ) : paginated.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-400 text-sm">Nenhuma tarefa encontrada</p>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="text-primary text-sm mt-2 hover:underline"
              >
                Limpar busca
              </button>
            )}
          </div>
        ) : (
          paginated.map((task, index) => (
            <div key={task.id}>
              <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-[122px_154px_81px_120px_91px_91px_123px] gap-[59px] items-center">
                {/* Checkbox + Task Name */}
                <div className="flex items-center gap-[10px]">
                  <button
                    onClick={() => onToggleTask?.(task.id)}
                    className="hover:opacity-70 transition-opacity"
                  >
                    {task.checked ? (
                      <CheckCircle2 className="size-6 text-[#7846FF]" />
                    ) : (
                      <Circle className="size-6 text-white" />
                    )}
                  </button>
                  <p className="text-sm text-[#F6F6F6]">{task.taskName}</p>
                </div>

                {/* Responsible */}
                <div className="flex items-center gap-[10px]">
                  <div className="size-[22px] rounded-full bg-gray-600 overflow-hidden flex-shrink-0">
                    {task.responsible.avatar ? (
                      <img
                        src={task.responsible.avatar}
                        alt={task.responsible.name}
                        className="size-full object-cover"
                      />
                    ) : (
                      <div className="size-full bg-gray-600" />
                    )}
                  </div>
                  <p className="text-sm font-semibold text-[#F6F6F6]">
                    {task.responsible.name}
                  </p>
                </div>

                {/* Date */}
                <p className="text-sm text-[#F6F6F6]">{task.date}</p>

                {/* Stage */}
                <p className="text-sm font-semibold text-[#F6F6F6]">{task.stage}</p>

                {/* Status */}
                <div>
                  <StatusBadge status={task.status} />
                </div>

                {/* Project - Truncate if too long */}
                <p className="text-sm text-[#F6F6F6] truncate">{task.project}</p>

                {/* Actions */}
                <div className="flex gap-[10px]">
                  <button
                    onClick={() => onViewTask?.(task.id)}
                    className="hover:opacity-70 transition-opacity"
                    title="Visualizar"
                  >
                    <Eye className="size-[24px] text-white" />
                  </button>
                  <button
                    onClick={() => onEditTask?.(task.id)}
                    className="hover:opacity-70 transition-opacity"
                    title="Editar"
                  >
                    <Pencil className="size-[24px] text-white" />
                  </button>
                  <button
                    onClick={() => onDeleteTask?.(task.id)}
                    className="hover:opacity-70 transition-opacity"
                    title="Excluir"
                  >
                    <Trash2 className="size-[24px] text-white" />
                  </button>
                </div>
              </div>
              {index < paginated.length - 1 && (
                <div className="border-b border-gray-800/60" />
              )}
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 mt-6">
        <p className="text-sm text-[#888888]">
          Exibindo {paginated.length} de {filtered.length} tarefas
        </p>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>
    </SectionCard>
  );
}
